import React, { useContext, useEffect, useState } from 'react';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { DailyProgram } from '@go-shared/types/dailyProgram';
import { FilteredRundown } from './FilteredRundown';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { getDailyProgram } from '@go-client/lib/api/dailyProgram';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { addDaysToUTCDate, DEFAULT_TIMESTR_FORMAT, isoDate } from '@go-shared/lib/time';
import { Language } from '@go-shared/types/info';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

interface EventRundownProps {
  area: FeedArea;
  selectedLanguage: Language | undefined;
  mcmKey: string;
}

export function EventRundown(props: EventRundownProps) {
  const { area, mcmKey, selectedLanguage } = props;

  const {
    startDateOffset = 0,
    endDateOffset = 0,
    eventRundownTitle,
    eventRundownDeckPrefix,
    eventRundownShowDescription,
    eventRundownFilter,
    eventRundownHideEndTime,
    eventRundownRemoveTimeDash,
    eventRundownTimeGrouping,
  } = area.content?.params ?? {};

  const { activeProperty } = useActiveProperty();
  const { dailyProgram } = useContext(FeedEditorCtx);
  const { defaultLanguage } = useServerInfo();
  const [dailyPrograms, setDailyPrograms] = useState<DailyProgram[] | undefined>(undefined);

  const isSingleDay = startDateOffset === 0 && endDateOffset === 0;

  useEffect(() => {
    if (!isSingleDay) {
      fetchData().catch(console.error);
    }
  }, [dailyProgram, startDateOffset, endDateOffset]);

  async function fetchData() {
    if (!dailyProgram) return;

    const baseDate = new Date(dailyProgram.date);
    const from = -Math.abs(startDateOffset);
    const to = endDateOffset;

    const dates: string[] = [];
    for (let i = from; i <= to; i++) {
      dates.push(isoDate(addDaysToUTCDate(baseDate, i)));
    }

    const response = await getDailyProgram(activeProperty._id, mcmKey, {
      dates,
      embarkDebarkStrategy: dailyProgram.dayBundle?.dayIndex === 0 ? 'EMBARK_ONLY' : 'DEBARK_ONLY',
      settings: {
        venueStringStyle: 'name-only',
        eventTimeStringStyle: DEFAULT_TIMESTR_FORMAT,
        venueTimeStringStyle: DEFAULT_TIMESTR_FORMAT,
      },
    });

    setDailyPrograms(
      response.lineup.map(lineup => ({
        collections: response.collections,
        ...lineup,
      }))
    );
  }

  if (!dailyProgram) {
    return <div className="p-4">No daily program available</div>;
  }

  if (isSingleDay) {
    return (
      <FilteredRundown
        area={{
          ...area,
          params: {
            ...area.params,
            eventRundownTitle,
            eventRundownDeckPrefix,
            eventRundownShowDescription,
            eventRundownFilter,
            eventRundownHideEndTime,
            eventRundownRemoveTimeDash,
            eventRundownTimeGrouping,
          },
        }}
        dailyProgram={dailyProgram}
        selectedLanguage={selectedLanguage}
      />
    );
  }

  return (
    <div className="event-rundown w-full">
      {dailyPrograms === undefined ? (
        <div className="p-4">Loading daily programs...</div>
      ) : (
        dailyPrograms.map((program, i) => {
          const locationNames = program.dayBundle?.day?.locations?.map(loc => loc.name).join(', ');
          return (
            <div key={`filtered-${i}`} style={{ breakInside: 'avoid' }}>
              <h1 className="event-rundown-day-title text-base font-semibold border-b border-grey-dark">
                {new Date(program.date).toLocaleString(
                  props.selectedLanguage?.key ?? defaultLanguage.key,
                  {
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC',
                  }
                )}
                {locationNames ? (
                  <span className="event-rundown-location-inline hidden">{locationNames}</span>
                ) : null}
              </h1>
              <FilteredRundown
                area={{
                  ...area,
                  params: {
                    ...area.params,
                    eventRundownTitle,
                    eventRundownDeckPrefix,
                    eventRundownShowDescription,
                    eventRundownFilter,
                    eventRundownHideEndTime,
                    eventRundownRemoveTimeDash,
                    eventRundownTimeGrouping,
                  },
                }}
                dailyProgram={program}
                selectedLanguage={selectedLanguage}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
